import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FiMessageCircle, FiFileText, FiDollarSign, FiCheckCircle, FiMessageSquare, FiPlus } from 'react-icons/fi';
import type {
  ConversationsResponse,
  DisputeConversation,
  SelectedConversation,
} from '@/lib/types';
import { formatMoney } from '@/lib/utils';
import { apiJson } from '@/lib/api';

function formatRelativeTime(timestamp: string | null): string {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

type ConversationsListProps = {
  conversations: ConversationsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  selected: SelectedConversation | null;
  onSelect: (conversation: SelectedConversation) => void;
  currentUserId?: number;
  onNavigateToNewCustomChat?: (chatId: number) => void;
};

type NewChatModalProps = {
  open: boolean;
  onClose: () => void;
  onCreated: (id: number) => void;
};

function NewChatModal({ open, onClose, onCreated }: NewChatModalProps) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');

  const createMutation = useMutation({
    mutationFn: (t: string) =>
      apiJson<{ id: number }>(getToken, '/v1/chats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: t.trim() }),
      }),
    onSuccess: (data) => {
      setTitle('');
      onClose();
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      onCreated(data.id);
    },
  });

  if (!open) return null;

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'
      onClick={onClose}
    >
      <div
        className='w-full max-w-sm rounded-2xl bg-white p-5 shadow-lg'
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className='text-lg font-semibold text-gray-900'>New chat</h2>
        <p className='text-sm text-gray-500 mt-1'>Title (1–80 characters)</p>
        <input
          className='mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={80}
          autoFocus
          placeholder='e.g. School updates'
        />
        <div className='mt-4 flex justify-end gap-2'>
          <button
            type='button'
            onClick={onClose}
            className='px-3 py-1.5 text-sm text-gray-600'
          >
            Cancel
          </button>
          <button
            type='button'
            disabled={!title.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate(title)}
            className='px-3 py-1.5 text-sm font-semibold text-white bg-primary rounded-lg disabled:opacity-50'
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ConversationsList({
  conversations,
  isLoading,
  isError,
  selected,
  onSelect,
  currentUserId,
  onNavigateToNewCustomChat,
}: ConversationsListProps) {
  const [showNew, setShowNew] = useState(false);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex flex-col items-center justify-center py-12 px-4 text-center'>
        <div className='w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3'>
          <FiMessageCircle className='w-6 h-6 text-gray-400' />
        </div>
        <p className='text-gray-900 font-semibold'>Couldn't load conversations</p>
        <p className='text-gray-500 text-sm mt-1'>Pull down to try again</p>
      </div>
    );
  }

  const mainChat = conversations?.main_chat;
  const customChats = conversations?.custom_chats ?? [];
  const activeCustom = customChats.filter((c) => !c.is_archived);
  const archivedCustom = customChats.filter((c) => c.is_archived);
  const unresolvedDisputes = conversations?.unresolved_disputes ?? [];
  const resolvedDisputes = conversations?.resolved_disputes ?? [];
  const mainChatUnread = mainChat?.unread_count ?? 0;

  const isMainSelected = selected?.type === 'main';

  return (
    <div className='flex-1 overflow-y-auto'>
      {/* Main Chat */}
      <div className='p-4'>
        <button
          onClick={() => onSelect({ type: 'main' })}
          className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border transition-colors text-left ${
            isMainSelected
              ? 'bg-primary/10 border-primary'
              : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
          }`}
        >
          <div className='w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0'>
            <FiMessageCircle className='w-6 h-6 text-white' />
          </div>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center justify-between gap-2'>
              <span className='font-bold text-gray-900'>Main Chat</span>
              {mainChat?.last_message_timestamp && (
                <span className='text-xs text-gray-400'>
                  {formatRelativeTime(mainChat.last_message_timestamp)}
                </span>
              )}
            </div>
            <div className='flex items-center justify-between gap-2 mt-0.5'>
              <p className={`text-sm truncate ${mainChatUnread > 0 ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
                {mainChat?.last_message_content
                  ? `${mainChat.last_message_sender_first_name ?? 'Someone'}: ${mainChat.last_message_content}`
                  : 'No messages yet'}
              </p>
              {mainChatUnread > 0 && (
                <span className='flex-shrink-0 px-1.5 min-w-[20px] h-5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center'>
                  {mainChatUnread > 99 ? '99+' : mainChatUnread}
                </span>
              )}
            </div>
          </div>
        </button>
      </div>

      {/* Chats (custom) */}
      <div className='px-4 pb-4'>
        <div className='flex items-center justify-between mb-3'>
          <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>
            Chats
          </p>
          <button
            type='button'
            onClick={() => setShowNew(true)}
            className='flex items-center gap-1 text-sm font-semibold text-primary'
          >
            <FiPlus className='w-4 h-4' />
            New chat
          </button>
        </div>
        <div className='space-y-2'>
          {activeCustom.map((chat) => {
            const hasUnread = chat.unread_count > 0;
            const isSel =
              selected?.type === 'custom-chat' && selected.chatId === chat.chat_room_id;
            return (
              <button
                key={`cc-${chat.chat_room_id}`}
                type='button'
                onClick={() =>
                  onSelect({
                    type: 'custom-chat',
                    chatId: chat.chat_room_id,
                    title: chat.title,
                    isArchived: false,
                    isCreator: currentUserId === chat.creator_user_id,
                  })
                }
                className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border transition-colors text-left ${
                  isSel
                    ? 'bg-indigo-500/10 border-indigo-500'
                    : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <div className='w-12 h-12 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0'>
                  <FiMessageSquare className='w-6 h-6 text-white' />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center justify-between gap-2'>
                    <span className='font-bold text-gray-900 truncate'>{chat.title}</span>
                    {chat.last_message_timestamp && (
                      <span className='text-xs text-gray-400 flex-shrink-0'>
                        {formatRelativeTime(chat.last_message_timestamp)}
                      </span>
                    )}
                  </div>
                  <div className='flex items-center justify-between gap-2 mt-0.5'>
                    <p
                      className={`text-sm truncate ${
                        hasUnread ? 'text-gray-900 font-semibold' : 'text-gray-500'
                      }`}
                    >
                      {chat.last_message_content
                        ? `${chat.last_message_sender_first_name ?? 'Someone'}: ${chat.last_message_content}`
                        : 'No messages yet'}
                    </p>
                    {hasUnread && (
                      <span className='flex-shrink-0 px-1.5 min-w-[20px] h-5 rounded-full bg-indigo-500 text-white text-[11px] font-bold flex items-center justify-center'>
                        {chat.unread_count > 99 ? '99+' : chat.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <NewChatModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(id) => onNavigateToNewCustomChat?.(id)}
      />

      {/* Unresolved Disputes */}
      {unresolvedDisputes.length > 0 && (
        <div className='px-4 pb-4'>
          <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3'>
            Unresolved Disputes
          </p>
          <div className='space-y-2'>
            {unresolvedDisputes.map((dispute) => (
              <DisputeCard
                key={`${dispute.dispute_type}-${dispute.dispute_id}`}
                dispute={dispute}
                isSelected={
                  (selected?.type === 'expense-dispute' &&
                    dispute.dispute_type === 'expense' &&
                    selected.disputeId === dispute.dispute_id) ||
                  (selected?.type === 'payment-dispute' &&
                    dispute.dispute_type === 'payment' &&
                    selected.disputeId === dispute.dispute_id)
                }
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      )}

      {archivedCustom.length > 0 && (
        <div className='px-4 pb-4'>
          <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3'>
            Archived chats
          </p>
          <div className='space-y-2'>
            {archivedCustom.map((chat) => {
              const isSel =
                selected?.type === 'custom-chat' && selected.chatId === chat.chat_room_id;
              return (
                <button
                  key={`arch-cc-${chat.chat_room_id}`}
                  type='button'
                  onClick={() =>
                    onSelect({
                      type: 'custom-chat',
                      chatId: chat.chat_room_id,
                      title: chat.title,
                      isArchived: true,
                      isCreator: currentUserId === chat.creator_user_id,
                    })
                  }
                  className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border transition-colors text-left bg-gray-50 ${
                    isSel ? 'border-indigo-500 ring-1 ring-indigo-500/20' : 'border-gray-200'
                  }`}
                >
                  <div className='w-12 h-12 rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0'>
                    <FiMessageSquare className='w-6 h-6 text-white' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center justify-between gap-2'>
                      <span className='font-bold text-gray-500 truncate'>{chat.title}</span>
                    </div>
                    <p className='text-xs text-gray-400 mt-0.5'>Archived</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Resolved Disputes */}
      {resolvedDisputes.length > 0 && (
        <div className='px-4 pb-4'>
          <p className='text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3'>
            Resolved Disputes
          </p>
          <div className='space-y-2'>
            {resolvedDisputes.map((dispute) => (
              <DisputeCard
                key={`${dispute.dispute_type}-${dispute.dispute_id}`}
                dispute={dispute}
                isSelected={
                  (selected?.type === 'expense-dispute' &&
                    dispute.dispute_type === 'expense' &&
                    selected.disputeId === dispute.dispute_id) ||
                  (selected?.type === 'payment-dispute' &&
                    dispute.dispute_type === 'payment' &&
                    selected.disputeId === dispute.dispute_id)
                }
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state for no disputes */}
      {unresolvedDisputes.length === 0 && resolvedDisputes.length === 0 && (
        <div className='flex flex-col items-center py-8 px-4 text-center'>
          <div className='w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mb-2'>
            <FiFileText className='w-4 h-4 text-gray-300' />
          </div>
          <p className='text-gray-400 font-semibold text-sm'>No dispute conversations</p>
          <p className='text-gray-300 text-xs mt-1 max-w-[200px]'>
            When you or your co-parent disputes an expense or payment, you can discuss it here.
          </p>
        </div>
      )}
    </div>
  );
}

type DisputeCardProps = {
  dispute: DisputeConversation;
  isSelected: boolean;
  onSelect: (conversation: SelectedConversation) => void;
};

function DisputeCard({ dispute, isSelected, onSelect }: DisputeCardProps) {
  const hasUnread = dispute.unread_count > 0;
  const isExpense = dispute.dispute_type === 'expense';

  const handleClick = () => {
    if (isExpense) {
      onSelect({
        type: 'expense-dispute',
        disputeId: dispute.dispute_id,
        itemId: dispute.item_id,
      });
    } else {
      onSelect({
        type: 'payment-dispute',
        disputeId: dispute.dispute_id,
        itemId: dispute.item_id,
      });
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border transition-colors text-left ${
        isSelected
          ? 'bg-primary/10 border-primary'
          : dispute.is_resolved
          ? 'bg-gray-50 border-gray-200 opacity-80 hover:opacity-100'
          : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }`}
    >
      <div
        className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
          dispute.is_resolved ? 'bg-gray-400' : 'bg-amber-500'
        }`}
      >
        {isExpense ? (
          <FiFileText className='w-6 h-6 text-white' />
        ) : (
          <FiDollarSign className='w-6 h-6 text-white' />
        )}
      </div>
      <div className='flex-1 min-w-0'>
        <div className='flex items-center justify-between gap-2'>
          <div className='flex items-center gap-2 flex-1 min-w-0'>
            <span className={`font-bold truncate ${dispute.is_resolved ? 'text-gray-500' : 'text-gray-900'}`}>
              {dispute.item_name}
            </span>
            <span className='text-sm text-gray-500 flex-shrink-0'>
              {formatMoney(dispute.item_amount_cents)}
            </span>
          </div>
          {dispute.last_message_timestamp && (
            <span className='text-xs text-gray-400 flex-shrink-0'>
              {formatRelativeTime(dispute.last_message_timestamp)}
            </span>
          )}
        </div>
        <div className='flex items-center justify-between gap-2 mt-0.5'>
          <p
            className={`text-sm truncate ${
              hasUnread && !dispute.is_resolved
                ? 'text-gray-900 font-semibold'
                : dispute.is_resolved
                ? 'text-gray-400'
                : 'text-gray-500'
            }`}
          >
            {dispute.last_message_content
              ? `${dispute.last_message_sender_first_name ?? 'Someone'}: ${dispute.last_message_content}`
              : dispute.reason}
          </p>
          {hasUnread && !dispute.is_resolved && (
            <span className='flex-shrink-0 px-1.5 min-w-[20px] h-5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center'>
              {dispute.unread_count > 99 ? '99+' : dispute.unread_count}
            </span>
          )}
        </div>
        {dispute.is_resolved && (
          <div className='flex items-center gap-1 mt-1.5'>
            <FiCheckCircle className='w-3 h-3 text-gray-500' />
            <span className='text-xs text-gray-500'>Resolved</span>
          </div>
        )}
      </div>
    </button>
  );
}
